import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import {
	useGetDealConfigQuery,
	useGetHubspotPipelinesQuery,
	useGetHubspotOwnersQuery,
	useGetUserMappingsQuery,
	useCreateHubspotDealMutation,
} from "@/pages/Private/redux/hubspot/hubspot.api";

interface ProspectInfo {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	title?: string | null;
	organizationName?: string | null;
}

const buildDealName = (template: string | null | undefined, prospect: ProspectInfo): string => {
	const defaultTemplate = "{firstName} {lastName} - {company}";
	let name = template || defaultTemplate;

	name = name.replace("{firstName}", prospect.firstName || "");
	name = name.replace("{lastName}", prospect.lastName || "");
	name = name.replace("{company}", prospect.organizationName || "");
	name = name.replace("{email}", prospect.email || "");
	name = name.replace("{title}", prospect.title || "");

	return name.trim();
};

interface CreateHubspotDealModalProps {
	isOpen: boolean;
	onClose: () => void;
	prospectId: number;
	prospect?: ProspectInfo;
}

export const CreateHubspotDealModal: FunctionComponent<CreateHubspotDealModalProps> = ({
	isOpen,
	onClose,
	prospectId,
	prospect,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.hubspot.createDeal.${key}`);

	const { user } = useAppSelector(authSelector);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: dealConfig } = useGetDealConfigQuery();
	const { data: pipelinesData } = useGetHubspotPipelinesQuery();
	const { data: ownersData } = useGetHubspotOwnersQuery();
	const { data: userMappingsData } = useGetUserMappingsQuery();
	const [createDeal, { isLoading: isCreating }] = useCreateHubspotDealMutation();

	const [dealName, setDealName] = useState("");
	const [pipeline, setPipeline] = useState<string | null>(null);
	const [dealStage, setDealStage] = useState<string | null>(null);
	const [dealOwnerId, setDealOwnerId] = useState<string | null>(null);
	const [closeDate, setCloseDate] = useState("");

	// Pre-populate from deal config defaults
	useEffect(() => {
		if (dealConfig) {
			if (dealConfig.defaultPipeline) {
				setPipeline(dealConfig.defaultPipeline);
			}

			if (dealConfig.defaultDealStage) {
				setDealStage(dealConfig.defaultDealStage);
			}

			if (dealConfig.defaultContactOwner) {
				setDealOwnerId(dealConfig.defaultContactOwner);
			}

			if (prospect) {
				setDealName(buildDealName(dealConfig.dealNameTemplate, prospect));
			} else if (dealConfig.dealNameTemplate) {
				setDealName(dealConfig.dealNameTemplate);
			}

			if (dealConfig.closeDateOffset) {
				const date = new Date();

				date.setDate(date.getDate() + dealConfig.closeDateOffset);
				setCloseDate(date.toISOString().split("T")[0]);
			}
		}
	}, [dealConfig, prospect]);

	// Override owner from user mapping if available
	useEffect(() => {
		if (userMappingsData && user?.id) {
			const mapping = userMappingsData.find((m) => m.userId === user.id);

			if (mapping) {
				setDealOwnerId(mapping.hubspotOwnerId);
			}
		}
	}, [userMappingsData, user?.id]);

	const pipelineOptions: DropdownAutoCompleteItem[] = useMemo(
		() => pipelinesData?.pipelines?.map((p) => ({ id: p.id, title: p.label })) || [],
		[pipelinesData]
	);

	const stageOptions: DropdownAutoCompleteItem[] = useMemo(() => {
		if (!pipeline) {
			return [];
		}

		const selectedPipeline = pipelinesData?.pipelines?.find((p) => p.id === pipeline);

		return selectedPipeline?.stages?.map((s) => ({ id: s.id, title: s.label })) || [];
	}, [pipelinesData, pipeline]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			ownersData?.owners?.map((o) => ({
				id: String(o.id),
				title: `${o.firstName || ""} ${o.lastName || ""} (${o.email})`.trim(),
			})) || [],
		[ownersData]
	);

	const canSubmit = prospectId;

	const handleSubmit = async () => {
		if (!canSubmit) {
			return;
		}

		try {
			await createDeal({
				prospectId,
				dealName: dealName.trim() || undefined,
				pipeline: pipeline || undefined,
				dealStage: dealStage || undefined,
				dealOwnerId: dealOwnerId || undefined,
				closeDate: closeDate || undefined,
			}).unwrap();
			handleOpenSnackbar(ts("success"));
			onClose();
		} catch {
			handleOpenSnackbar(ts("error"));
		}
	};

	return (
		<Modal
			disableSave={!canSubmit}
			handleClose={onClose}
			handleSave={handleSubmit}
			isLoading={isCreating}
			isOpened={isOpen}
			size="sm"
			submitButtonText={t("basics.createSomething", { something: "deal" })}
			title={ts("title")}
		>
			{renderMessageSnackbar()}
			<Grid container spacing={3}>
				<Grid size={{ xs: 12 }}>
					<InputField
						handleChange={(e) => setDealName(e.target.value)}
						label={ts("dealName")}
						name="dealName"
						placeholder={ts("dealName")}
						value={dealName}
					/>
				</Grid>

				<Grid size={{ md: 6, xs: 12 }}>
					<Dropdown
						data={pipelineOptions}
						defaultValue={pipeline ? pipelineOptions.find((p) => p.id === pipeline) : undefined}
						floating={true}
						handleSelect={(value) => {
							setPipeline(value.id as string);
							setDealStage(null);
						}}
						label={ts("pipeline")}
					/>
				</Grid>

				<Grid size={{ md: 6, xs: 12 }}>
					<Dropdown
						key={pipeline ?? "no-pipeline"}
						data={stageOptions}
						defaultValue={dealStage ? stageOptions.find((s) => s.id === dealStage) : undefined}
						disabled={!pipeline}
						floating={true}
						handleSelect={(value) => {
							setDealStage(value.id as string);
						}}
						label={ts("stage")}
					/>
				</Grid>

				<Grid size={{ md: 6, xs: 12 }}>
					<Dropdown
						data={ownerOptions}
						defaultValue={
							dealOwnerId
								? ownerOptions.find((o) => String(o.id) === String(dealOwnerId))
								: undefined
						}
						floating={true}
						handleSelect={(value) => {
							setDealOwnerId(value.id as string);
						}}
						isOptional={true}
						label={ts("owner")}
					/>
				</Grid>

				<Grid size={{ md: 6, xs: 12 }}>
					<InputField
						handleChange={(e) => setCloseDate(e.target.value)}
						isOptional={true}
						label={ts("closeDate")}
						name="closeDate"
						type="date"
						value={closeDate}
					/>
				</Grid>
			</Grid>
		</Modal>
	);
};
